// The slot names' own modules, not the folders' indexes, which would pull their choosers into the main bundle.
import { organizationFormSectionsSlot } from '@/features/site-management/organizations/paths'
import { userDetailsSlot, userFormTabsSlot } from '@/features/site-management/users/user'
import { AccessProvider } from '@/shared/access'
import { detailsSlots } from '@/shared/details'
import { shellSlots, type Feature } from '@/shared/registry'
import { myDataSlot } from '@/shared/search/my-data'

import { readDefaultRolesPermission } from './default-roles/default-roles-api'
import { myDataApi } from './my-data/paths'
import { createRolePath, editRoleRoute, findRolesPath, readRolesPermission, roleRoute, writeRolesPermission } from './roles/paths'

/** The role chooser, for every area's role pickers (AC-27). */
export { RoleChooser, type ChosenRole, type RoleChooserProps } from './roles'

/** A role's details page, and the slots it offers other areas, such as a link in its side menu (AC-05). */
export { roleDetailsPath, roleDetailsSlot } from './roles/paths'

/** The query parameter that presets a role on Create User, as Classes' "Create Instructor" link does (AC-06). */
export { presetRoleParameter } from './role-assignments/role-assignments-api'

/** Access Control: roles, role assignments, managed organizations and companies, and cross-site access. Its screens register here. */
export const feature: Feature = {
  id: 'access-control',
  // The caller's permissions, which show or hide nav items, Admin Home links and actions (AC-18).
  provider: AccessProvider,
  // Find, Details, Create and Edit User Role (AC-05).
  routes: [
    {
      path: findRolesPath,
      titleId: 'accessControl.roles.find.title',
      page: () => import('./roles/FindRolesPage').then((module) => module.FindRolesPage),
    },
    {
      path: createRolePath,
      titleId: 'accessControl.roles.create.title',
      page: () => import('./roles/RoleFormPages').then((module) => module.CreateRolePage),
    },
    {
      path: roleRoute,
      titleId: 'accessControl.roles.details.title',
      page: () => import('./roles/RoleDetailsPage').then((module) => module.RoleDetailsPage),
    },
    {
      path: editRoleRoute,
      titleId: 'accessControl.roles.edit.title',
      page: () => import('./roles/RoleFormPages').then((module) => module.EditRolePage),
    },
  ],
  adminHome: {
    links: [
      { id: 'find-user-roles', category: 'user-roles', labelId: 'accessControl.roles.find.title', path: findRolesPath, permission: readRolesPermission },
      { id: 'create-user-role', category: 'user-roles', labelId: 'accessControl.roles.create.title', path: createRolePath, permission: writeRolesPermission },
    ],
  },
  slots: [
    // Access Control's part of "Download my data" on My Profile (109236).
    { kind: 'link', slot: myDataSlot, id: 'access-control.my-data', labelId: 'accessControl.myData.part', path: myDataApi },
    {
      // Impersonate, on User Details (AC-12).
      kind: 'action',
      slot: detailsSlots(userDetailsSlot).actions,
      id: 'impersonate',
      component: () => import('./impersonation/ImpersonateAction').then((module) => module.ImpersonateAction),
    },
    {
      // "You are impersonating", with Stop, above the nav bar while it lasts (AC-12).
      kind: 'panel',
      slot: shellSlots.headerBanners,
      id: 'impersonation',
      component: () => import('./impersonation/ImpersonationBanner').then((module) => module.ImpersonationBanner),
    },
    {
      // Roles & Access on User Details: the roles a user holds (AC-06), their groups, and what they manage.
      kind: 'tab',
      slot: detailsSlots(userDetailsSlot).tabs,
      id: 'roles',
      labelId: 'accessControl.userAccess.tab',
      component: () => import('./user-access/UserAccessTab').then((module) => module.UserAccessTab),
    },
    {
      // Assigning roles on Create and Edit User, saved with the form (AC-06).
      kind: 'tab',
      slot: userFormTabsSlot,
      id: 'roles',
      labelId: 'accessControl.roleAssignments.tab',
      component: () => import('./role-assignments/UserRolesFormTab').then((module) => module.UserRolesFormTab),
    },
    {
      // The organizations a user manages, on Create and Edit User, saved with the form (AC-07).
      kind: 'tab',
      slot: userFormTabsSlot,
      id: 'managed-organizations',
      labelId: 'accessControl.managedOrganizations.tab',
      component: () => import('./managed-organizations/ManagedOrganizationsFormTab').then((module) => module.ManagedOrganizationsFormTab),
    },
    {
      // The companies a user manages, on Create and Edit User, saved with the form (AC-16).
      kind: 'tab',
      slot: userFormTabsSlot,
      id: 'managed-companies',
      labelId: 'accessControl.managedCompanies.tab',
      component: () => import('./managed-companies/ManagedCompaniesFormTab').then((module) => module.ManagedCompaniesFormTab),
    },
    {
      // The roles new people in an organization get, on Create and Edit Organization, saved with the form (AC-11).
      kind: 'tab',
      slot: organizationFormSectionsSlot,
      id: 'default-roles',
      labelId: 'accessControl.defaultRoles.tab',
      permission: readDefaultRolesPermission,
      component: () => import('./default-roles/DefaultRolesFormTab').then((module) => module.DefaultRolesFormTab),
    },
    {
      // The cross-site access toggle, on User Details (AC-13).
      kind: 'panel',
      slot: detailsSlots(userDetailsSlot).panels,
      id: 'cross-site-access',
      component: () => import('./cross-site-access/CrossSiteAccessPanel').then((module) => module.CrossSiteAccessPanel),
    },
  ],
}
